import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatAmountCommaSeperator, formatDate, formatDateToShow, scrollPanelFieldsToTop, sortDDLByLabel, useNavigateToLogout } from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DDLDataTable } from "../DDLDataTable";
import { EmployeeService } from "../../services/EmployeeService";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IDropDownListResponse } from "../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../models";
import { WireTransferDataTable } from "./WireTransferDataTable";
import { WireTransferService } from "../../services/WireTransferServices";
import { WireTransferSetupFieldsPanel } from "./Panels/WireTransferSetupFieldsPanel";
import { WireTransferSetupToolbar } from "./WireTransferSetupToolbar";
import { WireTransferSetupValidationSchema } from "./validator";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { yupResolver } from "@hookform/resolvers/yup";

// import { SingleColumnLayout } from "../EmployeeAssignemnt/SingleColumnLayout";







export interface IWireTransferSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "wireTransfer-single-data-record";
const WIRE_TRANSFER_LIST_DATA_QUERY_KEY = "wireTransfer-list";

const useGetData = (
	clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, navigateToLogout],
		queryFn: () => {
			return WireTransferService.getById(clientId, id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});



const useWireTransfer = (
	clientId: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [WIRE_TRANSFER_LIST_DATA_QUERY_KEY, params],
		queryFn: (data) => {

			return WireTransferService.getWireTransfer(clientId, params, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});


export const WireTransferSetup: React.FC<IWireTransferSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const isAdminUser = useSelector((state: IAppState) => state.user.profile.isSuperStatus)
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [wireTransferId, setWireTransferId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);
	const [wireTransferTableData, setWireTransferTableData] =
		useState([]);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "date",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "date",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);


	const { isPending: isSubmitting, mutate: submitWireTransferFormFields } =
		useMutation({
			mutationFn: WireTransferService.create,
			onSuccess: (res: any | IServerError | IResponse | any) => {

				if (res.status === 200) {

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/payroll/wire-transfer/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WIRE_TRANSFER_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/payroll/wire-transfer/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WIRE_TRANSFER_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {

				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isUpdating, mutate: updateWireTransferFormFields } =
		useMutation({
			mutationFn: WireTransferService.update,
			onSuccess: (res: any | IServerError | IResponse | any) => {

				if (res.status === 200) {

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/payroll/wire-transfer/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WIRE_TRANSFER_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/payroll/wire-transfer/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WIRE_TRANSFER_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {

				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		editRecordId,
		navigateToLogout
	);

	const { isPending, data } = useWireTransfer(
		clientId,
		queryParams,
		navigateToLogout
	);

	const wireTansferSetupList: any | null = data;
	// const wireTansferSetupList: any = {
	// 	total: 4,
	// 	items: [
	// 		{
	// 			id: 1,
	// 			label: "Bank",        // ← add this
	// 			creditAmount: 2300.00,
	// 			date: "Nov 07 2024",
	// 			confNo: "123",
	// 			amount: "123",
	// 			debitAmount: null,
	// 			accumulatedAmount: "2,300.00",
	// 			approved: "Yes",
	// 		},
	// 		{
	// 			id: 2,
	// 			label: "Invoice",     // ← add this
	// 			creditAmount: null,
	// 			date: "Jan 08 2026",
	// 			confNo: "Payperiod Ending 2024-11-30",
	// 			debitAmount: "-28.25",
	// 			accumulatedAmount: "2,271.75",
	// 			approved: "Yes",
	// 		},
	// 		{
	// 			id: 3,
	// 			label: "Bank",        // ← add this
	// 			creditAmount: 5000.00,
	// 			date: "May 01 2026",
	// 			confNo: "01",
	// 			debitAmount: null,
	// 			accumulatedAmount: "7,271.75",
	// 			approved: "Yes",
	// 		},
	// 		{
	// 			id: 4,
	// 			label: "Bank",        // ← add this
	// 			creditAmount: 300.00,
	// 			date: "May 07 2026",
	// 			confNo: "sd",
	// 			debitAmount: null,
	// 			accumulatedAmount: "7,571.75",
	// 			approved: "Yes",
	// 		},
	// 	]
	// };

	const updatedWireTransferListItems = useMemo(() => {
		return (
			wireTansferSetupList &&
			wireTansferSetupList.items &&
			wireTansferSetupList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/payroll/wire-transfer/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.wireMethod}
					</Link>
				),
			}))
		);
	}, [wireTansferSetupList]);


	const DEFAULT_VALUES = {
		date: null,
		wireamount: undefined,
		wireMethod: null,
		confirmationNo: null,
		notes: undefined,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(WireTransferSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {
		let payload = {
			clientId: clientId,
			id: id && id,
			date: formatDate(values.date),
			wireAmount: values.wireAmount,
			wireMethod: Number(values.wireMethod),
			confirmationNo: values.confirmationNo,
			notes: values.notes
		};
		console.log("values", values)
		console.log("payload", payload)
		submitWireTransferFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "date") {
					formMethods.setValue("date", new Date(fieldValue));
				}
			});
		}
	}, [id, selectedDataRecord]);


	// const onSubmitApprovedClickedHandler = () => {
	// 	const updatePayloadData =
	// 		wireTransferTableData &&
	// 		wireTransferTableData.map((item: any) => ({
	// 			...item,
	// 		}));
	// 	const EditedRowData = updatePayloadData.filter((item: any) => item.canEdit)
	// 	if (!EditedRowData || EditedRowData.length === 0) {
	// 		setSnackbarData({
	// 			open: true,
	// 			message: "Enter Credit Amount",
	// 			severity: "error",
	// 		});
	// 		return
	// 	}
	// 	else {

	// 		submitWireTransferFormFields({ data: [EditedRowData[0]] })
	// 	}
	// 	console.log("updatePayloadData", updatePayloadData);
	// 	console.log("EditedRowData", EditedRowData);
	// }

	const onSubmitApprovedClickedHandler = () => {
		const updatePayloadData =
			wireTransferTableData &&
			wireTransferTableData.map((item: any) => ({
				...item,
			}));

		// const EditedRowData = updatePayloadData.filter((item: any) => item.canEdit && item.creditAmount !== null);

		// if (!EditedRowData || EditedRowData.length === 0) {
		// 	setSnackbarData({
		// 		open: true,
		// 		message: "Please enter credit amount",
		// 		severity: "error",
		// 	});
		// 	return;
		// }
		// submitWireTransferFormFields({
		// 	data: [EditedRowData[0]],
		// });

		// Check if ANY editable row has null/undefined creditAmount
		const emptyCreditAmountField = updatePayloadData.some(
			(item: any) => item.canEdit && (item.creditAmount === null || item.creditAmount === "")
		);

		if (emptyCreditAmountField) {
			setSnackbarData({
				open: true,
				message: "Please enter credit amount",
				severity: "error",
			});
			return;
		}

		const EditedRowData = updatePayloadData.filter(
			(item: any) => item.canEdit && item.creditAmount !== null && item.creditAmount !== ""
		);
		console.log("EditedRowData", EditedRowData);

		if (!EditedRowData) {
			setSnackbarData({
				open: true,
				message: "Please enter credit amount",
				severity: "error",
			});
			return;
		}

		const updated = EditedRowData.map((item: any) => ({
			...item,
			label: item.label.props.children,
			// canEdit: undefined,
			date: formatDate(item.date),
			wireAmount: Number(item.wireAmount),
			wireMethod: item.wireMethod === "Bank" ? 100 : 200
		}))
		updateWireTransferFormFields({
			wireData: updated,
		});

		console.log("updatePayloadData", updatePayloadData);
		console.log("EditedRowData", EditedRowData);
		console.log("updated", updated);
	};

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteWireTransferById } = useMutation({
		mutationFn: WireTransferService.delWireTransfer,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {

				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			scrollPanelFieldsToTop();
			let path = `/payroll/wire-transfer`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [WIRE_TRANSFER_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteWireTransferField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				wireTransferId: wireTransferId,
				navigateToLogout: navigateToLogout,
			};
			deleteWireTransferById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setWireTransferId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setWireTransferId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveWireTransferById } = useMutation({
		mutationFn: WireTransferService.archiveWireTransfer,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {

				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			scrollPanelFieldsToTop();
			let path = `/payroll/wire-transfer`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [WIRE_TRANSFER_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveWireTransferField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				wireTransferId: wireTransferId,
				navigateToLogout: navigateToLogout,
			};
			archiveWireTransferById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setWireTransferId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};
	const onDeleteClickHandler = () => {
		alert("fix later");
	};
	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		scrollPanelFieldsToTop();
		let path = `/payroll/wire-transfer`;
		navigate(path);
	};

	const updatedWireTransferSetupItems = useMemo(() => {
		if (wireTansferSetupList && wireTansferSetupList.items && updatedWireTransferListItems) {
			const items = [...updatedWireTransferListItems];

			// Find the last item with a non-null remitAmount using binary search
			// let left = 0;
			// let right = items.length - 1;
			// let lastCreditAmountIndex = -1;

			// while (left <= right) {
			// 	let mid = Math.floor((left + right) / 2);
			// 	if (items[mid].creditAmount != null) {
			// 		lastCreditAmountIndex = mid;
			// 		left = mid + 1; // look for later remitted value
			// 	} else {
			// 		right = mid - 1;
			// 	}
			// }

			// // Find the first null creditAmount AFTER the last remitted one
			// let nextNullIndex = -1;
			// for (let i = lastCreditAmountIndex + 1; i < items.length; i++) {
			// 	if (items[i].creditAmount == null) {
			// 		nextNullIndex = i;
			// 		break;
			// 	}
			// }
			// Map and add the two tags
			// return items.map((item, index) => ({
			// 	...item,
			// 	canEdit: index === nextNullIndex,
			// }));
			return items.map((item) => ({
				...item,
				// 	canEdit: item.creditAmount === null || item.creditAmount === undefined  &&
				//  item.debitAmount === null || item.debitAmount === undefined || item.debitAmount === "",
				canEdit: (item.creditAmount === null || item.creditAmount === undefined || item.creditAmount === "") &&
					!(item.debitAmount !== null && item.debitAmount !== undefined && item.debitAmount !== ""),
			}));
		}
	}, [wireTansferSetupList, updatedWireTransferListItems]);

	const expandedLabels = ["Payroll"];
	const expandedWireTransferOrSetupItem = "Wire Transfer";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Payroll" &&
				item.label === expandedWireTransferOrSetupItem
			) {
				isExpanded = true;
			}

			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedWireTransferOrSetupItem && "/payroll/wire-transfer",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: any) => {
		let path = `/payroll/wire-transfer/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid>
			{/* <h1
  style={{
    margin: "auto",
    fontSize: "12rem",
    marginBottom: "30rem",
    marginTop: "12rem",
    textAlign: "center",
  }}
>
  SUN LO NAA 
</h1> */}
			<BackdropCircularProgress
				open={
					isSubmitting ||
					isPendingById ||
					isDeleting ||
					isArchive ||
					isPending ||
					isUpdating
				}
				testId="wire-transfer Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteWireTransferField}
				cancelActionHandler={onCloseHandler}
				testId="wire-transfer-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.2, lg: 3.2, xl: 3.2 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Wire Transfer"
										testId={`wire-taansfer`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<WireTransferSetupFieldsPanel />
									</StandardPanel>
								}
								toolbar={
									<WireTransferSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: .8, lg: .8, xl: .8 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
							sx={{ marginTop: "16px" }}
						>
							<WireTransferDataTable
								// rows={
								// 	wireTansferSetupList && updatedWireTransferListItems && updatedWireTransferSetupItems
								// 		? updatedWireTransferSetupItems
								// 		: []
								// 	// updatedWireTransferSetupItems
								// }
								rows={
									wireTansferSetupList && updatedWireTransferListItems && updatedWireTransferSetupItems ? updatedWireTransferSetupItems : []
								}
								columns={[
									{ field: "label", headerName: "Type" },
									{ field: "date", headerName: "Date", align: "center" },
									{ field: "confirmationNo", headerName: "Conf No" },
									{ field: "wireAmount", headerName: "Amount", align: "right" },
									{ field: "creditAmount", headerName: "Credit Amount", align: "right" },
									{ field: "debitAmount", headerName: "Debit Amount", align: "right" },
									{ field: "accumulatedAmount", headerName: "Accumulated", align: "right" },
									{ field: "approvedStatus", headerName: "Approved", align: "center" },
								]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								showPaginator={false}
								showArchiveIcon={false}
								// onRowArchiveClicked={onArchiveClicked}
								// isLoading={isPending}
								totalRecords={
									wireTansferSetupList && wireTansferSetupList.total
										? wireTansferSetupList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								WireTransferTableData={(data: any) =>
									setWireTransferTableData(data)
								}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
								footer={
									<Grid
										sx={{
											marginTop: "1rem",
											display: "flex",
											gap: "1rem",
											justifyContent: "center"
										}}>
										{isAdminUser === 400 && <EnhancedButtonPrimeReact
											// icon="pi pi-trash"
											label="Approved"
											raised
											style={{ fontSize: "small" }}
											onClickHandler={onSubmitApprovedClickedHandler}
											severity={EnhancedButtonPrimeReactSeverity.Secondary}
										/>}
									</Grid>
								}
							/>

						</Grid>

						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>

					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveWireTransferField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"wireTransfer-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
